import { ApiError } from "@/client/extend";
import { OptionType } from "@/components/select-input/select-input";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { GroupBase } from "react-select";
import { FormItemCommonProps, FormItemContent } from "../common";
import {
  SelectInputAsyncPaginateMutation,
  SelectInputAsyncPaginateMutationProps,
  SelectInputAsyncPaginateQuery,
  SelectInputAsyncPaginateQueryProps,
} from "@/components/select-input/select-input-async-pagination";
import {
  FieldOptionsCacheProps,
  FieldOptionsExtractProps,
  FieldOptionsMappingProps,
  SelectInputOptionsMappingProps,
  useFieldOptionsExtract,
  useFieldOptionsMapping,
  useFieldOptionsPartial,
  useFormSelectInputCommonClassNames,
} from "./common";
import { FormField } from "@/components/ui/form";
import { QueryKey } from "@tanstack/react-query";
import { callAll, cn } from "@/lib/utils";
import { useOptionsCache } from "@/components/select-input/options-cache";
import React from "react";

export type FormSelectInputAsyncPaginateMutationProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData,
  TError,
  TVariables,
  TContext2,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  name: TName;
} & FormItemCommonProps &
  Omit<
    SelectInputOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>,
    "isMulti"
  > &
  Omit<
    SelectInputAsyncPaginateMutationProps<
      Value,
      Option,
      IsMulti,
      Group,
      TData,
      TError,
      TVariables,
      TContext2
    >,
    "form"
  > & {
    inline?: boolean;
    displaySelected?: boolean;
    isActive?: boolean;
    isNullable?: boolean;
  };

export type FormSelectInputAsyncPaginateMutationItemCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
  Value = string | number,
  Option extends OptionType<Value> = OptionType<Value>,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
  TData = any,
  TError = ApiError,
  TVariables = void,
  TContext2 = unknown,
> = Omit<
  FormSelectInputAsyncPaginateMutationProps<
    TFieldValues,
    TName,
    TContext,
    TTransformedValues,
    Value,
    Option,
    IsMulti,
    Group,
    TData,
    TError,
    TVariables,
    TContext2
  >,
  | "form"
  | "name"
  | "label"
  | "mapping"
  | "mutation"
  | "createOptions"
  | "requestVariable"
> &
  Partial<Pick<FormItemCommonProps, "label">>;

export function FormSelectInputAsyncPaginateMutationItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData,
  TError,
  TVariables,
  TContext2,
>({
  form,
  name,
  label,
  container,
  assign,
  mapping,
  extract,
  onChange,
  onBlur,
  className,
  classNames: _classNames,
  isActive,
  inline,
  displaySelected,
  isNullable,
  empty,
  ...props
}: FormSelectInputAsyncPaginateMutationProps<
  TFieldValues,
  TName,
  TContext,
  TTransformedValues,
  Value,
  Option,
  IsMulti,
  Group,
  TData,
  TError,
  TVariables,
  TContext2
>) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const value = useFieldOptionsMapping({
          ...props,
          field,
          assign,
          mapping,
          extract,
          isNullable,
          empty,
          // cache
        } as FieldOptionsMappingProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ext = useFieldOptionsExtract({
          ...props,
          field,
          assign,
          mapping,
          extract,
          isNullable,
          empty,
          // updateCache
        } as FieldOptionsExtractProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const classNames = useFormSelectInputCommonClassNames({
          ...props,
          classNames: _classNames,
          isActive,
          inline,
        });
        return (
          <FormItemContent
            {...container}
            label={label}
            inline={container?.inline ?? inline}
          >
            <SelectInputAsyncPaginateMutation
              debounceTimeout={300}
              className={cn("w-full group/select", className)}
              classNames={classNames}
              {...(field as any)}
              value={value || ""}
              onChange={(v, meta) => {
                const value = ext(v as any);
                const ret = field.onChange(value);
                if (onChange) onChange(v as any, meta as any);
                return ret;
              }}
              onBlur={callAll(field.onBlur, onBlur)}
              getNewOptionData={mapping}
              {...props}
              placeholder={
                inline
                  ? label
                    ? `${label}${props?.isMulti && ((value as any[])?.length ?? 0) > 0 ? ` (${(value as any[])?.length})` : ""}`
                    : undefined
                  : props.placeholder
              }
              controlShouldRenderValue={
                displaySelected
                  ? true
                  : inline
                    ? false
                    : props.controlShouldRenderValue
              }
            />
          </FormItemContent>
        );
      }}
    />
  );
}

export type FormSelectInputAsyncPaginateQueryProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
  TCacheKey extends QueryKey | undefined,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  name: TName;
  queryKey?: TCacheKey;
} & FormItemCommonProps &
  Omit<
    SelectInputOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>,
    "isMulti"
  > &
  Omit<
    SelectInputAsyncPaginateQueryProps<
      Value,
      Option,
      IsMulti,
      Group,
      TVariables,
      TQueryFnData,
      TError,
      TData,
      TQueryKey
    >,
    "form"
  > & {
    inline?: boolean;
    displaySelected?: boolean;
    isActive?: boolean;
    isNullable?: boolean;
  };

export type FormSelectInputAsyncPaginateQueryItemCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
  Value = string | number,
  Option extends OptionType<Value> = OptionType<Value>,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
  TVariables = any,
  TQueryFnData = unknown,
  TError = ApiError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TCacheKey extends QueryKey | undefined = QueryKey,
> = Omit<
  FormSelectInputAsyncPaginateQueryProps<
    TFieldValues,
    TName,
    TContext,
    TTransformedValues,
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TCacheKey
  >,
  | "form"
  | "name"
  | "label"
  | "mapping"
  | "queryCreator"
  | "createOptions"
  | "requestVariable"
  | "hasMore"
> &
  Partial<Pick<FormItemCommonProps, "label">>;

export function FormSelectInputAsyncPaginateQueryItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
  TCacheKey extends QueryKey | undefined,
>({
  form,
  name,
  label,
  container,
  mapping,
  extract,
  onChange,
  onBlur,
  className,
  classNames: _classNames,
  inline,
  displaySelected,
  isActive,
  queryKey,
  isNullable,
  empty,
  ...props
}: FormSelectInputAsyncPaginateQueryProps<
  TFieldValues,
  TName,
  TContext,
  TTransformedValues,
  Value,
  Option,
  IsMulti,
  Group,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey,
  TCacheKey
>) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { cache, findMissing, findPartial } = useOptionsCache({
          queryKey,
          ...props,
        });

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const value = useFieldOptionsMapping({
          ...props,
          field,
          mapping,
          extract,
          isNullable,
          empty,
          isMulti: props?.isMulti,
          cache,
        } as FieldOptionsMappingProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const partial = useFieldOptionsPartial({
          ...props,
          field,
          mapping,
          extract,
          isNullable,
          empty,
          cache,
        } as FieldOptionsCacheProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti,
          typeof cache
        >);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
          if (!mapping || partial.length <= 0) return;
          findPartial(partial);
        }, [field.value, mapping]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ext = useFieldOptionsExtract({
          ...props,
          field,
          mapping,
          extract,
          isNullable,
          empty,
          // updateCache
        } as FieldOptionsExtractProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const classNames = useFormSelectInputCommonClassNames({
          ...props,
          classNames: _classNames,
          isActive,
          inline,
        });
        return (
          <FormItemContent
            {...container}
            label={label}
            inline={container?.inline ?? inline}
          >
            <SelectInputAsyncPaginateQuery
              debounceTimeout={300}
              className={cn("w-full group/select", className)}
              classNames={classNames}
              {...(field as any)}
              value={value || ""}
              onChange={(v, meta) => {
                const value = ext(v as any);
                const ret = field.onChange(value);
                if (onChange) onChange(v as any, meta as any);
                return ret;
              }}
              onBlur={callAll(field.onBlur, onBlur)}
              getNewOptionData={mapping}
              getNewOptionDataAsync={findMissing}
              {...props}
              placeholder={
                inline
                  ? label
                    ? `${label}${props?.isMulti && ((value as any[])?.length ?? 0) > 0 ? ` (${(value as any[])?.length})` : ""}`
                    : undefined
                  : props.placeholder
              }
              // controlShouldRenderValue={
              //   displaySelected
              //     ? true
              //     : inline
              //       ? false
              //       : props.controlShouldRenderValue
              // }
            />
          </FormItemContent>
        );
      }}
    />
  );
}
